import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SecurityInfo, SymbolType } from '../../../../domain/symbol.domain';
import { SecurityService } from '../../../../services/security.service';
import { TradingAccount } from '../../../../domain/account.domain';
import { FractionalEligibility, EquityFormModel } from '../domain/order-entry.domain';
import { EquityOrderAction } from '../../domain/order.enum';
import { FractionalEligibilityRestService } from './fractional-eligibility-rest.service';

@Injectable()
export class FractionalTradingService {
  private readonly fractionalEligibilityRestService = inject(FractionalEligibilityRestService);
  private readonly securityService = inject(SecurityService);

  securityFractionalEligibility: Pick<SecurityInfo, 'fractionalBuy' | 'fractionalSell'>;
  eligibleAccountTypesForFractionalOrders: Array<string> = ['A', 'B', 'E', 'F', 'S', 'U', 'J', 'K', 'T', 'I', 'V', 'N', 'O'];
  fractionalTrade: boolean = false;
  fractionalEligible: boolean = false;
  firstFractionalEligibilityChange: boolean = true;

  isFractionalTradingEligible(selectedTradingAccount: TradingAccount, orderEntryFormModel: EquityFormModel): boolean {

    let fractionalEligibility: boolean;

    if (this.securityFractionalEligibility && selectedTradingAccount && orderEntryFormModel) {

      const fractionalEligibleAccount = this.eligibleAccountTypesForFractionalOrders.includes(selectedTradingAccount?.type?.stringValue);
      const fractionalEligibleSecurity = (this.securityFractionalEligibility.fractionalSell &&
          (orderEntryFormModel.orderAction === EquityOrderAction.SELL)) ||
        (this.securityFractionalEligibility.fractionalBuy && (orderEntryFormModel.orderAction === EquityOrderAction.BUY));
      const marketOrder = orderEntryFormModel?.orderPrice?.valueOf() === 'MARKET';

      fractionalEligibility = fractionalEligibleAccount && marketOrder && fractionalEligibleSecurity;
    }

    // reset to day as other options not available
    if (fractionalEligibility && this.fractionalTrade) {
      orderEntryFormModel.orderGoodTil = 'DAY';
    }
    return fractionalEligibility;
  }

  retrieveSecurityFractionalEligibility(selectedSymbol: SecurityInfo,
                                        orderEntryFormModel: EquityFormModel,
                                        selectedTradingAccount: TradingAccount,
                                        firstLookup?: boolean): Observable<FractionalEligibility> {

    //when order entry from is called from buy/sell button from pages etc holding, securityType is sent
    // thus need to convert to symbolType
    const symbolType = selectedSymbol?.symbolType ?? SymbolType[selectedSymbol?.type?.toString()];

    return this.fractionalEligibilityRestService.getFractionalEligibility(selectedSymbol.symbol, symbolType, selectedSymbol.market)
      .pipe(tap((fractionalEligibility: FractionalEligibility) => {
        this.updateFractionalEligibility(fractionalEligibility, orderEntryFormModel, selectedTradingAccount, firstLookup);
      }));
  }

  setFractionalEligibility(displayOrderEntryCalculator: boolean,
                           orderEntryFormModel: EquityFormModel,
                           selectedTradingAccount: TradingAccount,
                           firstLookup?: boolean): void {
    const oldValue = this.fractionalEligible;
    this.fractionalEligible = this.isFractionalTradingEligible(selectedTradingAccount, orderEntryFormModel);
    const noFractionalChange  = oldValue === undefined && this.fractionalEligible === false;
    if (!firstLookup && oldValue !== this.fractionalEligible && !noFractionalChange) {
      this.firstFractionalEligibilityChange = false;
    }

    // when calculator is hidden and quantity is fractional, round it up/down based on order action.
    if (!displayOrderEntryCalculator && orderEntryFormModel.orderQuantity !== undefined && orderEntryFormModel.orderQuantity % 1 !== 0) {
      orderEntryFormModel.orderQuantity =
        orderEntryFormModel.orderAction === EquityOrderAction.SELL ?
          Math.ceil(orderEntryFormModel.orderQuantity) : Math.floor(orderEntryFormModel.orderQuantity);
    }
  }

  isFractionalTradingOn(): boolean {
    return this.securityService.hasPermission('ORDER_ENTRY_CALCULATOR_WB');
  }

  updateFractionalEligibility(fractionalEligibility: FractionalEligibility,
                              orderEntryFormModel: EquityFormModel,
                              selectedTradingAccount: TradingAccount,
                              firstLookup?: boolean): void {
    this.securityFractionalEligibility = {
      fractionalBuy: fractionalEligibility?.fractionalBuy,
      fractionalSell: fractionalEligibility?.fractionalSell
    };
    this.setFractionalEligibility(true, orderEntryFormModel, selectedTradingAccount, firstLookup);
  }
}
